import React from 'react';
import { Shield, FileText, AlertCircle, CheckCircle } from 'lucide-react';

interface FrameworkBadgeProps {
  frameworkName: string;
  standardId?: string;
  type?: 'framework' | 'control' | 'violation' | 'compliance';
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
  onClick?: () => void;
}

export const FrameworkBadge: React.FC<FrameworkBadgeProps> = ({
  frameworkName,
  standardId,
  type = 'framework',
  size = 'md',
  showIcon = true,
  className = '',
  onClick
}) => {
  // Get framework color based on name
  const getFrameworkColor = (name: string): string => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('basel')) return 'bg-blue-100 text-blue-700 border-blue-300';
    if (lowerName.includes('nist')) return 'bg-purple-100 text-purple-700 border-purple-300';
    if (lowerName.includes('iso')) return 'bg-green-100 text-green-700 border-green-300';
    if (lowerName.includes('coso')) return 'bg-orange-100 text-orange-700 border-orange-300';
    return 'bg-gray-100 text-gray-700 border-gray-300';
  };

  // Get type-specific styling
  const getTypeColor = (): string => {
    switch (type) {
      case 'control':
        return 'bg-indigo-100 text-indigo-700 border-indigo-300';
      case 'violation':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'compliance':
        return 'bg-emerald-100 text-emerald-700 border-emerald-300';
      default:
        return getFrameworkColor(frameworkName);
    }
  };

  // Get size classes
  const getSizeClasses = (): string => {
    switch (size) {
      case 'sm':
        return 'px-2 py-0.5 text-xs';
      case 'lg':
        return 'px-4 py-2 text-base';
      default:
        return 'px-3 py-1 text-sm';
    }
  };

  // Get icon based on type
  const getIcon = () => {
    if (!showIcon) return null;

    const iconSize = size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4';

    switch (type) {
      case 'control':
        return <FileText className={iconSize} />;
      case 'violation':
        return <AlertCircle className={iconSize} />;
      case 'compliance':
        return <CheckCircle className={iconSize} />;
      default:
        return <Shield className={iconSize} />;
    }
  };

  return (
    <span
      className={`
        inline-flex items-center gap-1.5
        ${getSizeClasses()}
        ${getTypeColor()}
        border rounded-full font-medium
        ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}
        ${className}
      `}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {getIcon()}
      <span className="truncate">
        {frameworkName}
        {standardId && <span className="ml-1 opacity-75">({standardId})</span>}
      </span>
    </span>
  );
};

// Specialized badge variants
export const BaselBadge: React.FC<Omit<FrameworkBadgeProps, 'frameworkName'>> = (props) => (
  <FrameworkBadge frameworkName="Basel" {...props} />
);

export const NISTBadge: React.FC<Omit<FrameworkBadgeProps, 'frameworkName'>> = (props) => (
  <FrameworkBadge frameworkName="NIST" {...props} />
);

export const ISOBadge: React.FC<Omit<FrameworkBadgeProps, 'frameworkName'>> = (props) => (
  <FrameworkBadge frameworkName="ISO 27002" {...props} />
);

export const COSOBadge: React.FC<Omit<FrameworkBadgeProps, 'frameworkName'>> = (props) => (
  <FrameworkBadge frameworkName="COSO" {...props} />
);

// Badge group for displaying multiple frameworks
interface FrameworkBadgeGroupProps {
  frameworks: Array<{
    name: string;
    standardId?: string;
    type?: 'framework' | 'control' | 'violation' | 'compliance';
  }>;
  maxDisplay?: number;
  size?: 'sm' | 'md' | 'lg';
  onFrameworkClick?: (framework: string) => void;
}

export const FrameworkBadgeGroup: React.FC<FrameworkBadgeGroupProps> = ({
  frameworks,
  maxDisplay = 3,
  size = 'sm',
  onFrameworkClick
}) => {
  const displayFrameworks = frameworks.slice(0, maxDisplay);
  const remaining = frameworks.length - maxDisplay;

  return (
    <div className="flex flex-wrap gap-1.5">
      {displayFrameworks.map((fw, index) => (
        <FrameworkBadge
          key={`${fw.name}-${fw.standardId || index}`}
          frameworkName={fw.name}
          standardId={fw.standardId}
          type={fw.type}
          size={size}
          onClick={onFrameworkClick ? () => onFrameworkClick(fw.name) : undefined}
        />
      ))}
      {remaining > 0 && (
        <span
          className={`
            inline-flex items-center justify-center
            ${size === 'sm' ? 'px-2 py-0.5 text-xs' : size === 'lg' ? 'px-4 py-2 text-base' : 'px-3 py-1 text-sm'}
            bg-gray-100 text-gray-600 border border-gray-300 rounded-full font-medium
          `}
        >
          +{remaining}
        </span>
      )}
    </div>
  );
};
